import {AttrsSchema, Component} from 'panel';
import {VNode} from 'snabbdom';
import ResizeObserver from 'resize-observer-polyfill';
import type {Instance as PopperInstance, Placement as PopperPlacement} from '@popperjs/core';
import {
  arrow as popperArrowModifier,
  createPopperLite as createPopper,
  flip as popperFlipModifier,
  offset as popperOffsetModifier,
  preventOverflow as popperPreventOverflowModifier,
} from '@popperjs/core';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {portalify} from 'common/util/experimental-portal';
import timing from 'common/stylesheets/mixins/timing.json';
import {CHROME_HEADER_HEIGHT} from 'common/constants';
import {panel2react} from 'common/util/react';

import {
  DisplaySize,
  DisplayType,
  Placement,
  PositioningStrategy,
  TooltipAttrs,
  TooltipJsxProps,
  TooltipState,
} from './types';
import {template} from './template';
import css from './index.inline.styl';

const VISIBILITY_HIDDEN = `hidden`;
const VISIBILITY_TRANSITIONING = `transitioning`;
const VISIBILITY_VISIBLE = `visible`;

// defaults right and left positioning to popper's top edge alignment
const POPPER_POSITION_OVERRIDES: {[key in Placement]: PopperPlacement} = {
  [Placement.Left]: `left-start`,
  [Placement.Right]: `right-start`,
  [Placement.RightCenter]: `right`,
  [Placement.RightEnd]: `right-end`,
  [Placement.Top]: `top`,
  [Placement.Bottom]: `bottom`,
};

/**
 * When a delay is provided, after a tooltip is shown with initial in-delay, hovering over neighbor
 * elements with tooltips (while the previous tooltip is still shown with an out-delay or not) will not have a delay effect.
 *
 * This behavior is achieved by keeping a reference to the currently active tooltip so the next tooltip knows it's part of a "chain",
 * and hide the previous tooltip when it has an out-delay
 */
let currentlyActiveTooltip: Tooltip = null;

export class Tooltip extends Component<TooltipState, TooltipAttrs> {
  private _listeners;
  public _parentEl;
  private _pendingPositionTooltipRaf;
  private _pendingPositionTooltipTimeout;
  private _pendingHideTooltipTimeout;
  private elmRefs: Record<string, HTMLDivElement> = {};

  private scrollParent: HTMLElement = null;
  private popperInstance: PopperInstance = null;
  private resizeObserver: ResizeObserver;

  /** for expensive components/templates that will only render when tooltip is visible */
  public lazyContentRenderer?: () => VNode;

  static get attrsSchema(): AttrsSchema<TooltipAttrs> {
    return {
      ...super.attrsSchema,
      'is-tooltip-tour': `boolean`,
      'always-show': `boolean`,
      'break-word': {
        type: `boolean`,
        description: `Whether to break within words for multiline tooltips (helpful for long URLs)`,
      },
      skidding: {type: `number`, default: 0, description: `Amount of shifting parallel to parent`},
      offset: {type: `number`, default: 8},
      'max-width': {type: `string`, default: null},
      placement: {type: `string`, default: Placement.Top, enum: Object.values(Placement)},
      type: {type: `string`, default: DisplayType.Default, enum: Object.values(DisplayType)},
      'delay-in': {
        type: `number`,
        description: `value in ms to delay showing tooltip`,
        default: timing[`$mp-default-tooltip-delay-in`],
      },
      'delay-out': {
        type: `number`,
        description: `value in ms to delay hiding tooltip`,
        default: timing[`$mp-default-tooltip-delay-out`],
      },
      'image-src': {type: `string`, description: `image to show on top of tooltip, used for tutorials.`},
      size: {type: `string`, enum: Object.values(DisplaySize), default: DisplaySize.Fit},
      text: {type: `string`, description: `display formatted text with links inside the tooltip.`},
      'position-strategy': {
        type: `string`,
        enum: Object.values(PositioningStrategy),
        default: PositioningStrategy.Fixed,
        description: `Defines how the tooltip is rendered, fixed to the document level or absolute relative to its offset parent`,
      },
      'allowed-flip-directions': {
        type: `json`,
        default: null,
        description: `Defines preferred flipping directions for whenever the dropdown cannot be fitted in view`,
      },
    };
  }

  constructor() {
    super();
    // track added listeners so we can remove them in disconnectedCallback and be GC friendly
    this._listeners = new Map();
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        visibility: VISIBILITY_HIDDEN,
        isFirstActiveTooltip: true,
      },
      helpers: {
        setElmRef: (name: string) => ({elm}) => (this.elmRefs[name] = elm),
        VISIBILITY_HIDDEN,
        VISIBILITY_TRANSITIONING,
        VISIBILITY_VISIBLE,
        stopPropagation: (ev) => {
          ev.stopPropagation();
        },
        getTooltipEl: (): HTMLDivElement | null => this.elmRefs[`main`] || this.el.querySelector(`.mp-tooltip-main`),
        shouldAnimateTooltip: () => !!this.attr(`delay-in`) && this.state.isFirstActiveTooltip,
      },
    };
  }

  /**
   * Setups the positioning library for the tooltip, for more info see:
   * https://popper.js.org/docs/v2/
   */
  setupPopper() {
    // initializes popper for the first time the tooltip is being displayed
    !this.popperInstance && (this.popperInstance = createPopper(this.parentEl, this));
    const scrollParent = this.popperInstance.state.scrollParents?.reference[0] ?? window;
    const isBodyLevel = (scrollParent as unknown) === window;
    this.scrollParent = isBodyLevel ? document.body : (scrollParent as HTMLElement);

    this._addEventListener(this.popperInstance.state.elements.popper, `mouseleave`, this.hide);
    this.popperInstance.setOptions({
      placement: POPPER_POSITION_OVERRIDES[this.attr(`placement`)],
      strategy: this.attr(`position-strategy`),
      modifiers: [
        // moves the tooltip in a way that prevents it
        // from being cut from the viewport
        {
          ...popperPreventOverflowModifier,
          name: `preventOverflow`,
          options: {
            padding: 8,
          },
        },
        // moves the tooltip so that it distances itself
        // from its parent, accounts for the tooltip arrow
        {
          ...popperOffsetModifier,
          name: `offset`,
          options: {
            offset: [this.attr(`skidding`), this.attr(`offset`)],
          },
        },
        // aligns the tooltip's arrow to the center of
        // the tooltip's parent, both horizontally
        // and vertically depending on how the tooltip
        // is positioned
        {
          ...popperArrowModifier,
          name: `arrow`,
          options: {
            padding: 4, // distance from tooltip's edge to arrow
            element: this.el.querySelector(`.mp-tooltip-arrow`),
          },
        },
        // makes the tooltip align itself so that it
        // is visible inside the window viewport
        {
          ...popperFlipModifier,
          name: `flip`,
          options: {
            rootBoundary: `viewport`,
            flipVariations: false,
            padding: {top: CHROME_HEADER_HEIGHT},
            ...(Array.isArray(this.attr(`allowed-flip-directions`))
              ? {
                  fallbackPlacements: this.attr(`allowed-flip-directions`).map(
                    (placement) => POPPER_POSITION_OVERRIDES[placement],
                  ),
                }
              : {}),
          },
        },
      ],
    });

    return this.popperInstance;
  }

  show() {
    // Avoid race condition when mouse leaves and enters within the delay window
    if (currentlyActiveTooltip === this) {
      return;
    }

    this.update({
      visibility: VISIBILITY_TRANSITIONING,
    });

    const delay = this.attr(`delay-in`);
    const makeTooltipVisible = () => {
      if (delay && !this.attr(`always-show`)) {
        this.update({isFirstActiveTooltip: !currentlyActiveTooltip});
        currentlyActiveTooltip?.hideAndCancelPositioning();
        // we're not aliasing 'this', we're storing a module-level reference to the current component
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        currentlyActiveTooltip = this;
      }

      this.setupPopper().update();

      this.update({
        visibility: VISIBILITY_VISIBLE,
      });
      this.dispatchEvent(new CustomEvent(`showed`));
    };
    if (delay && !currentlyActiveTooltip) {
      this._pendingPositionTooltipTimeout = window.setTimeout(() => {
        makeTooltipVisible();
      }, delay);
    } else {
      this._pendingPositionTooltipRaf = window.requestAnimationFrame(() => {
        makeTooltipVisible();
      });
    }

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.resizeObserver?.observe(document.body);
        this.scrollParent && this.resizeObserver?.observe(this.scrollParent);
        this.setupPopper().update();
      });
    }
    this.resizeObserver.observe(this);
  }

  hide(ev: MouseEvent) {
    // hide behavior should be disabled on tooltip tour mode mode since the tooltip-tour element
    // handles it's own visibility behavior
    if (this.attr(`is-tooltip-tour`)) return;

    if (ev.relatedTarget === this.popperInstance?.state.elements.popper) {
      return;
    }
    this.cancelPendingPositionTooltipIfNeeded();
    this.resizeObserver?.disconnect();

    const delay = this.attr(`delay-out`);
    const hideTooltip = () => {
      this.update({visibility: VISIBILITY_HIDDEN});
      currentlyActiveTooltip = null;

      /** to prevent popper from modifying styles before the HIDDEN state gets a chance
       * to render, we wait for the next rendering cycle to cleanup popper
       */
      requestAnimationFrame(() => {
        this.popperInstance?.destroy();
        this.popperInstance = null;
      });
    };
    if (delay) {
      this._pendingHideTooltipTimeout = setTimeout(() => hideTooltip(), delay);
    } else {
      hideTooltip();
    }
  }

  /**
   * Used for adding/removing event listeners
   */
  get parentEl() {
    return this._parentEl || this.parentNode;
  }

  set parentEl(parentEl) {
    this._parentEl = parentEl;
    if (this.initialized) {
      this.render();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // From MDN doc https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements
    // "connectedCallback may be called once your element is no longer connected, use
    // Node.isConnected to make sure."
    // This is happening when dragging query entry rows in the query builder. As a result,
    // parentNode is unexpectedly null, leading to a JS exception.
    //
    // Secondary check for this.parentNode existing because it is intermittently null in iso tests as of panel@5.7.0
    // which fixed setting isConnected before connectedCallback(). TODO(jakub): investigate why this is happening.
    if (!this.isConnected || !this.parentNode) {
      return;
    }

    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);

    this.render();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (this.initialized) {
      this.render();
    }
  }

  render() {
    this.removeListenersIfNeeded();
    if (this.attr(`always-show`)) {
      this.show();
    } else {
      if (this.state.visibility === VISIBILITY_VISIBLE) {
        this.show();
      }
      this._addEventListener(this.parentEl, `mouseenter`, this.show);
      this._addEventListener(this.parentEl, `mousedown`, this.hide);
      this._addEventListener(this.parentEl, `mouseleave`, this.hide);
    }
  }

  _addEventListener(el, event, listener) {
    el.addEventListener(event, listener);
    if (!this._listeners.has(el)) {
      this._listeners.set(el, {[event]: listener});
    } else {
      this._listeners.get(el)[event] = listener;
    }
  }

  removeListenersIfNeeded() {
    for (const [el, listeners] of this._listeners.entries()) {
      for (const [event, listener] of Object.entries(listeners)) {
        el.removeEventListener(event, listener);
      }
    }
  }

  cancelPendingPositionTooltipIfNeeded() {
    if (this._pendingPositionTooltipRaf) {
      window.cancelAnimationFrame(this._pendingPositionTooltipRaf);
      this._pendingPositionTooltipRaf = null;
    }

    if (this._pendingPositionTooltipTimeout) {
      window.clearTimeout(this._pendingPositionTooltipTimeout);
      this._pendingPositionTooltipTimeout = null;
    }

    if (this._pendingHideTooltipTimeout) {
      window.clearTimeout(this._pendingHideTooltipTimeout);
      this._pendingHideTooltipTimeout = null;
    }
  }

  disconnectedCallback() {
    this.popperInstance?.destroy();
    this.resizeObserver?.disconnect();
    super.disconnectedCallback();
    this.removeListenersIfNeeded();
    this.cancelPendingPositionTooltipIfNeeded();
    if (currentlyActiveTooltip === this) {
      currentlyActiveTooltip = null;
    }
  }

  // Called externally by reference to this component
  public hideAndCancelPositioning() {
    this.update({visibility: VISIBILITY_HIDDEN});
    this.cancelPendingPositionTooltipIfNeeded();
  }
}

export const TooltipEl = defineAndMakeJsxFactory<TooltipJsxProps>(`mp-tooltip`, Tooltip);

const PortaledTooltip = portalify(TooltipEl);
export const ExperimentalTooltipEl = function (...args: Parameters<typeof PortaledTooltip>) {
  const vnode = PortaledTooltip(...args);
  const defaultInsertHook = vnode.data.hook.insert;
  vnode.data.hook.insert = (vnode: VNode) => {
    defaultInsertHook?.(vnode);

    const tooltipEl = vnode.data.attachData.real as Tooltip;
    const placeholderEl = vnode.data.attachData.placeholder as HTMLElement;
    if (!Object.prototype.hasOwnProperty.call(vnode.data.props, `parentEl`) && !tooltipEl._parentEl) {
      // If the callsite hasn't specified parentEl prop, then set it to the parent element of the tooltip's original
      // location in the DOM.
      tooltipEl.parentEl = placeholderEl.parentElement;
    }
  };
  return vnode;
};
export const TooltipReact = panel2react<TooltipJsxProps>(`mp-tooltip`, Tooltip);
